import type { Product } from '../api/types'
import { useT } from '../i18n'
import { achsenAnsicht, type Auswahl } from '../variants'

/**
 * Die Achsen eines Produkts als Auswahlfelder.
 *
 * Der Kern der Produktseite. Drei Zustände, die auseinandergehalten werden
 * müssen, weil sie für den Kunden Verschiedenes bedeuten:
 *
 *   verfügbar          gibt es, ist da
 *   ausverkauft        gibt es, ist gerade aus — kommt wieder
 *   nicht kombinierbar gibt es in dieser Zusammenstellung nicht
 *
 * Der dritte Fall wird gezeigt und nicht weggelassen. Ein stilles Weglassen
 * sieht aus wie „den Pullover gibt es nicht in Blau", dabei gibt es nur diese
 * eine Größe nicht in Blau. Ein Klick darauf bleibt möglich und rückt die
 * übrigen Achsen nach (siehe waehle) — sonst wäre die Auswahl eine Sackgasse.
 */
export function VariantPicker({
  product,
  auswahl,
  onWaehle,
}: {
  product: Product
  auswahl: Auswahl
  onWaehle: (achse: string, wert: string) => void
}) {
  const t = useT()

  // Ein Produkt ohne Achsen hat genau eine Variante — dann ist jede Auswahl
  // eine Frage ohne Antwortmöglichkeit und bleibt besser weg.
  if (product.variantAxes.length === 0) return null

  return (
    <div>
      {achsenAnsicht(product, auswahl).map(({ achse, werte }) => (
        <fieldset className="sf-achse" key={achse}>
          {/* Der gewählte Wert neben dem Namen der Achse: Bei acht Größen
              sucht niemand die umrandete, er liest „Größe · M". */}
          <legend>
            {achse}
            {auswahl[achse] && <span>{auswahl[achse]}</span>}
          </legend>
          <div className="sf-werte">
            {werte.map(({ wert, zustand }) => {
              const gewaehlt = auswahl[achse] === wert
              // Achse und Wert selbst bleiben unübersetzt: „Größe" und „M"
              // stehen so in der Kasse des Ladens. Nur was das Ladenfenster
              // dazusagt, wechselt die Sprache.
              const zusatz =
                zustand === 'ausverkauft'
                  ? t('variante.ausverkauft')
                  : zustand === 'nicht-kombinierbar'
                    ? t('variante.nichtKombinierbar')
                    : null

              return (
                <button
                  key={wert}
                  type="button"
                  className="sf-wert"
                  data-zustand={zustand}
                  aria-pressed={gewaehlt}
                  title={
                    zustand === 'nicht-kombinierbar'
                      ? t('variante.nichtKombinierbarTitel', { achse, wert })
                      : undefined
                  }
                  onClick={() => onWaehle(achse, wert)}
                >
                  {wert}
                  {/* Der Zustand für die Sprachausgabe als Wort; sehend sagt
                      ihn die Durchstreichung. */}
                  {zusatz && <span className="sf-bildschirmleser">, {zusatz}</span>}
                </button>
              )
            })}
          </div>
        </fieldset>
      ))}
    </div>
  )
}
